import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema } from "@/lib/es-pages";
import { FaqList, faqSchema, type Faq } from "@/components/site/InsuranceProse";
import { LazyTurnstile } from "@/components/site/LazyTurnstile";
import { sendSelfPayInquiry } from "@/lib/inquiries.functions";
import { SectionCta } from "@/components/site/SectionCta";

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;

const URL = "https://virginiabariatric.com/es/cirugia-bariatrica-de-pago-directo";

type PriceRow = {
  procedure: string;
  anchor?: string;
  facility: string;
  anesthesia: string;
  physician: string;
  total: string;
};

const SURGICAL: PriceRow[] = [
  { procedure: "Bypass gástrico", anchor: "costo-bypass-gastrico", facility: "$14,000 (1 día)", anesthesia: "$1,400*", physician: "$7,800", total: "$23,200*" },
  { procedure: "Manga gástrica", anchor: "costo-manga-gastrica", facility: "$10,000 (1 día)", anesthesia: "$1,400*", physician: "$7,800", total: "$19,200*" },
  { procedure: "Lap-Band (banda gástrica)", anchor: "costo-banda-gastrica", facility: "$10,000 (1 día)", anesthesia: "$1,400*", physician: "$7,800", total: "$19,200*" },
  { procedure: "Cirugía de revisión", anchor: "costo-cirugia-de-revision", facility: "$14,000 (1 día)", anesthesia: "$1,400*", physician: "$10,000", total: "$25,400*" },
];

const ENDOSCOPIC: PriceRow[] = [
  { procedure: "ESG (gastroplastia endoscópica en manga)", anchor: "costo-esg", facility: "$9,000*", anesthesia: "$1,400*", physician: "$2,500", total: "$12,900" },
  { procedure: "TORe (reducción transoral de la salida)", facility: "$8,000*", anesthesia: "$1,400*", physician: "$2,500", total: "$11,900" },
  { procedure: "Balón gástrico Orbera", anchor: "costo-balon-orbera", facility: "$6,000*", anesthesia: "$1,400*", physician: "$1,500", total: "$8,900" },
];


const FAQS: Faq[] = [
  {
    q: "¿Por qué pagar de su bolsillo si tengo seguro?",
    schemaAnswer:
      "El motivo más común es la espera. La cirugía bariátrica cubierta por seguro suele exigir hasta doce meses de visitas de dieta supervisada, y a veces la aseguradora niega la autorización previa o pide documentación imposible de conseguir. Otros pacientes eligen el pago propio porque su plan excluye la cirugía bariátrica por completo, o porque su deducible costaría casi lo mismo que el precio de pago propio.",
    a: "El motivo más común es la espera. La cirugía bariátrica cubierta por seguro suele exigir hasta doce meses de visitas de dieta supervisada. El proceso de autorización previa es normalmente sencillo, pero a veces la aseguradora lo convierte en una pesadilla burocrática: piden documentación imposible de conseguir o niegan la autorización por motivos absurdos. Otros pacientes eligen el pago propio porque su plan excluye la cirugía bariátrica por completo, o porque su deducible y su gasto máximo de bolsillo costarían casi lo mismo que el precio de pago propio. Algunos, sencillamente, no quieren esperar.",
  },
  {
    q: "¿El precio de pago propio es realmente lo que voy a pagar?",
    schemaAnswer:
      "Sí, o al menos debe ser muy cercano. El total cotizado cubre el honorario del cirujano, un día de hospital y un estimado de anestesia. La consulta, la evaluación psicológica, la autorización médica, los laboratorios y el electrocardiograma se facturan por separado y normalmente los cubre el seguro. Cada día adicional de hospital cuesta $1,000.",
    a: "Sí, o al menos debe ser muy cercano. El total cotizado cubre el honorario del cirujano, un día de hospital y un estimado de anestesia. La consulta, la evaluación psicológica, la autorización médica, los laboratorios y el electrocardiograma se facturan por separado; si tiene seguro, normalmente cubre esos servicios. Cada día adicional de hospital cuesta $1,000. Revisamos cada línea con usted antes de que se comprometa. Trabajamos para que no haya sorpresas.",
  },
  {
    q: "¿Qué pasa si necesito quedarme en el hospital más de un día?",
    schemaAnswer:
      "Cada día adicional en INOVA Fair Oaks Hospital cuesta $1,000, facturado por el hospital. Cerca del 90% de los pacientes de manga y bypass se van a casa al día siguiente de la cirugía, pero la recuperación es individual y el Dr. Fitzer le dará el alta cuando sea médicamente apropiado.",
    a: "Cada día adicional en INOVA Fair Oaks Hospital cuesta $1,000, facturado por el hospital. Cerca del 90% de los pacientes de manga y bypass se van a casa al día siguiente de la cirugía, pero su recuperación es individual y el Dr. Fitzer le dará el alta cuando sea médicamente apropiado.",
  },
  {
    q: "Ya me operaron en otro lugar. ¿Puedo ser paciente de revisión de pago propio?",
    schemaAnswer:
      "Sí. El Dr. Fitzer tiene amplia experiencia en cirugía de revisión. La revisión tiene un honorario de cirujano de $10,000 y un total estimado de $25,400.",
    a: "Por supuesto. El Dr. Fitzer tiene amplia experiencia en cirugía de revisión. La revisión tiene un honorario de cirujano de $10,000 y un total estimado de $25,400.",
  },
  {
    q: "¿Necesito un IMC de 35 o 40 para calificar?",
    schemaAnswer:
      "No para el pago propio. El requisito de pago propio es un IMC de 30 o más. Las aseguradoras usan umbrales de IMC más altos, otra razón por la que pacientes con un IMC entre 30 y 35 eligen pagar de su bolsillo.",
    a: "No para el pago propio. El requisito de pago propio es un IMC de 30 o más. Las aseguradoras usan umbrales de IMC por encima de los recomendados. Es otra razón por la que pacientes con un IMC entre 30 y 35 eligen pagar de su bolsillo.",
  },
  {
    q: "¿Qué pasa si empiezo el proceso y luego cambio de opinión?",
    schemaAnswer:
      "No está obligado a continuar. Si completa visitas de dieta y decide no seguir adelante, esas visitas se facturan a $85 cada una. Los pagos del honorario del cirujano se manejan según la política de cancelación de cirugía del consultorio.",
    a: "A veces surge algo importante, o simplemente tiene dudas. Lo entendemos: no está obligado a continuar. Si completa visitas de dieta y decide no seguir adelante, esas visitas se facturan a $85 cada una. Los pagos del honorario del cirujano se manejan según la política de cancelación de cirugía del consultorio.",
  },
  {
    q: "¿Cuál es la política de cancelación de cirugía?",
    schemaAnswer:
      "Cancele lo antes posible. Si cancela dentro de los catorce días previos a la fecha de cirugía, hay un cargo de $500. Antes de catorce días no hay cargo por cancelación.",
    a: "Procure cancelar lo antes posible. Si cancela dentro de los catorce días previos a la fecha de cirugía, hay un cargo de $500. Antes de catorce días no hay cargo por cancelación. Entendemos que la vida trae imprevistos: avísenos en cuanto lo sepa y lo resolvemos juntos.",
  },
  {
    q: "¿La consulta está incluida en el precio de pago propio?",
    schemaAnswer:
      "Sí, si sigue adelante con la cirugía. Si decide no seguir, facturamos la consulta primero a su seguro. Si el seguro no la cubre, la consulta se le factura a usted por $333.75.",
    a: "Sí, si sigue adelante con la cirugía. Si decide no seguir, facturamos la consulta primero a su seguro. Normalmente el seguro la cubre. Si por cualquier motivo no la cubre, la consulta se le factura a usted por $333.75. Si sigue adelante con la cirugía, aplicamos el pago de su consulta al precio de la cirugía.",
  },
  {
    q: "¿Puedo usar mi cuenta HSA o FSA?",
    schemaAnswer:
      "Sí. La cirugía bariátrica es casi siempre un gasto médico elegible. Confirme con el administrador de su plan y guarde los recibos y cualquier carta de necesidad médica que le proporcionemos.",
    a: "Sí. La cirugía bariátrica es casi siempre un gasto médico elegible. Confirme con el administrador de su plan y guarde los recibos y cualquier carta de necesidad médica que le proporcionemos.",
  },
  {
    q: "Vivo fuera del norte de Virginia. ¿Puedo operarme con ustedes?",
    schemaAnswer:
      "Sí. Atendemos pacientes de todo el mundo. Díganos desde dónde viaja y le ayudamos a reducir el número de viajes. La mayoría del seguimiento postoperatorio se puede hacer por telemedicina y podemos coordinar con un médico local para las visitas de rutina.",
    a: "Atendemos pacientes de todo el mundo. Díganos desde dónde viaja y le ayudamos a reducir el número de viajes. La mayoría del seguimiento postoperatorio se puede hacer por telemedicina, y podemos coordinar con un médico cercano a usted para las visitas de rutina.",
  },
  {
    q: "¿Qué tan pronto podría operarme?",
    schemaAnswer:
      "Depende sobre todo de la rapidez con que complete sus autorizaciones médicas y el programa de dieta. Los pacientes de pago propio no esperan la autorización del seguro, así que el calendario lo marca su propia disponibilidad.",
    a: "Depende sobre todo de la rapidez con que complete sus autorizaciones médicas y el programa de dieta. Los pacientes de pago propio no esperan la autorización del seguro, así que el calendario lo marca su propia disponibilidad y no la política de una aseguradora.",
  },
];

export const Route = createFileRoute("/es/cirugia-bariatrica-de-pago-directo")({
  head: () => {
    const base = esHead("selfPay");
    return {
      ...base,
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(esWebPageSchema("selfPay")),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify(faqSchema(FAQS, URL, "es")),
            },
          ]
        : [],
    };
  },
  component: EsSelfPay,
});

function PriceTable({ caption, rows }: { caption: string; rows: PriceRow[] }) {
  return (
    <div className="mt-6">
      {rows
        .filter((r) => r.anchor)
        .map((r) => (
          <span key={r.anchor} id={r.anchor} className="block scroll-mt-28" aria-hidden="true" />
        ))}
      <h3 className="font-serif text-lg text-brand-deep">{caption}</h3>


      {/* Tabla para computadora y tableta */}
      <div className="mt-3 hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="bg-brand-soft text-left text-brand-deep">
              <th scope="col" className="border border-border px-3 py-2">Procedimiento</th>
              <th scope="col" className="border border-border px-3 py-2">Cargo del hospital</th>
              <th scope="col" className="border border-border px-3 py-2">Anestesia*</th>
              <th scope="col" className="border border-border px-3 py-2">Honorario del cirujano</th>
              <th scope="col" className="border border-border px-3 py-2">Total estimado*</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.procedure} className="align-top">
                <th scope="row" className="border border-border px-3 py-2 text-left font-semibold">{r.procedure}</th>
                <td className="border border-border px-3 py-2">{r.facility}</td>
                <td className="border border-border px-3 py-2">{r.anesthesia}</td>
                <td className="border border-border px-3 py-2">{r.physician}</td>
                <td className="border border-border px-3 py-2 font-semibold">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tarjetas para teléfono */}
      <ul className="mt-3 space-y-3 md:hidden">
        {rows.map((r) => (
          <li key={r.procedure} className="rounded-2xl border border-border bg-card p-4">
            <p className="font-semibold text-brand-mid">{r.procedure}</p>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Cargo del hospital</dt>
              <dd>{r.facility}</dd>
              <dt className="text-muted-foreground">Anestesia*</dt>
              <dd>{r.anesthesia}</dd>
              <dt className="text-muted-foreground">Honorario del cirujano</dt>
              <dd>{r.physician}</dd>
              <dt className="text-muted-foreground">Total estimado*</dt>
              <dd className="font-semibold">{r.total}</dd>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EsSelfPay() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const submitSelfPayInquiry = useServerFn(sendSelfPayInquiry);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!turnstileToken) {
      setError("Complete la verificación de seguridad antes de enviar.");
      return;
    }

    setSending(true);
    setError(null);
    const formData = new FormData(event.currentTarget);

    try {
      await submitSelfPayInquiry({
        data: {
          name: String(formData.get("name") || "").trim(),
          phone: String(formData.get("phone") || "").trim() || undefined,
          email: String(formData.get("email") || "").trim(),
          procedure: String(formData.get("procedure") || "").trim() || undefined,
          message: String(formData.get("message") || "").trim(),
          turnstileToken,
        },
      });
      setSent(true);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : "Algo salió mal. Inténtelo de nuevo.",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <EsPageShell
      page="selfPay"
      title="Cirugía bariátrica de pago propio en el norte de Virginia"
      intro="Si su seguro no cubre la cirugía de pérdida de peso —o prefiere no esperar un largo proceso de aprobación— el pago propio es un camino claro y predecible: un precio cotizado, requisitos fijos y un calendario que usted controla."
    >
      <EsSection heading="Precios de pago propio" id="precios">
        <p>
          Los precios quirúrgicos cubren una estancia hospitalaria de un día e incluyen el
          honorario del cirujano, el cargo del hospital y un estimado de anestesia. Los
          procedimientos endoscópicos se realizan en INOVA Fair Oaks Hospital de forma
          ambulatoria: se va a casa el mismo día. Todo se realiza con el Dr. Fitzer en INOVA
          Fair Oaks Hospital, un hospital de servicio completo.
        </p>

        <PriceTable caption="Procedimientos quirúrgicos — incluye un día de hospital" rows={SURGICAL} />
        <PriceTable caption="Procedimientos endoscópicos — ambulatorios, alta el mismo día" rows={ENDOSCOPIC} />

        <p className="text-sm text-muted-foreground">
          * Estimado. La anestesia la factura un grupo independiente de anestesiología, así que
          esa parte es un estimado y no un cargo fijo; los totales que la incluyen también son
          estimados.
        </p>

        <SectionCta
          to="/es/contacto"
          search={{ topic: "self-pay-callback" }}
          label="cta_self_pay_after_estimate_es"
        >
          Solicite una llamada de Val o Jamie
        </SectionCta>
        <p>
          <strong>Días adicionales de hospital:</strong> cada día adicional en INOVA Fair Oaks
          Hospital cuesta $1,000.
        </p>
        <p>
          <strong>Los precios mostrados son las tarifas vigentes de pago propio y pueden cambiar.</strong>{" "}
          Llame al{" "}
          <a href="tel:7037099771" className="text-brand-mid font-medium hover:underline">(703) 709-9771</a>{" "}
          para confirmar su cotización antes de programar.
        </p>
        <p className="text-sm text-muted-foreground">Precios revisados por última vez en septiembre de 2026.</p>
      </EsSection>

      <EsSection heading="Qué incluye su precio de pago propio" id="incluido">
        <p>La parte del honorario del cirujano cubre más que la operación en sí:</p>
        <ul className="list-disc space-y-1 pl-6">
          <li>La cirugía realizada por el Dr. Fitzer</li>
          <li>Las 3 sesiones con la dietista requeridas antes del procedimiento</li>
          <li>Sus citas preoperatorias con nuestro equipo quirúrgico</li>
          <li>El seguimiento postoperatorio de rutina con el Dr. Fitzer durante un año</li>
        </ul>
        <p>
          Los cargos del hospital y de anestesia cubren su estancia de un día en INOVA Fair Oaks
          Hospital, la sala de operaciones y los servicios de anestesia durante la cirugía.
        </p>
      </EsSection>

      <EsSection heading="Qué no está incluido" id="no-incluido">
        <p>Estos servicios se facturan por separado; tómelos en cuenta en su presupuesto:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Su consulta inicial.</strong> Pero solo si decide no seguir adelante. En ese
            caso, facturamos su consulta al seguro. Normalmente está cubierta. Si por cualquier
            motivo no está cubierta, se le cobran $333.75. Si sigue adelante con la cirugía,
            aplicamos el pago de su consulta al precio de la cirugía.
          </li>
          <li>
            <strong>Evaluación psicológica.</strong> Requerida antes de la cirugía. Podemos
            darle una lista de referidos si aún no tiene un profesional.
          </li>
          <li>
            <strong>Autorización médica, laboratorios y electrocardiograma.</strong> Deben
            completarse dentro de los 30 días previos a la cirugía y normalmente se facturan a
            su seguro o a su médico de cabecera.
          </li>
          <li>
            <strong>Pruebas adicionales.</strong> En algunos casos, su estado de salud requiere
            pruebas extra. El Dr. Fitzer le dirá exactamente qué necesita en su primera cita, y
            el consultorio le ayudará a programarlas.
          </li>
          <li>
            <strong>Días adicionales de hospital</strong> más allá del día incluido: $1,000 por día.
          </li>
          <li>
            <strong>Tratamiento de complicaciones, revisiones o procedimientos no relacionados.</strong>
          </li>
        </ul>
        <div className="rounded-2xl border border-brand/30 bg-brand-soft/60 p-5">
          <p>
            <strong>Nota:</strong> si comienza el programa de dieta y luego decide no operarse,
            cada visita de dieta completada se factura a $85.
          </p>
        </div>
      </EsSection>

      <EsSection heading="Cómo funcionan los pagos" id="pagos">
        <p>El honorario del cirujano se paga a nuestro consultorio en dos partes iguales:</p>
        <ol className="list-decimal space-y-1 pl-6">
          <li>$3,900 al programar la fecha de su cirugía.</li>
          <li>$3,900 en su última cita preoperatoria, tres semanas antes de la cirugía.</li>
        </ol>
        <p>
          Los pacientes de revisión siguen el mismo esquema de dos pagos sobre el honorario de
          revisión de $10,000. Los cargos del hospital y de anestesia los facturan por separado
          INOVA Fair Oaks Hospital y el grupo de anestesiología; nuestro consultorio le dará sus
          datos de contacto para que arregle el pago directamente con ellos. Aceptamos efectivo,
          cheque, las principales tarjetas de crédito y tarjetas HSA/FSA.
        </p>
      </EsSection>

      <EsSection heading="Financiamiento y formas de pago" id="financiamiento">
        <p>No tiene que pagarlo todo de una vez. Algunos pacientes usan una de estas opciones:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>
              Financiamiento médico a través de{" "}
              <Link to="/es/precios-y-financiamiento" className="text-brand-mid hover:underline">Prosper® Healthcare Lending</Link>.
            </strong>{" "}
            Prosper® ofrece planes de pago mensual fijo para cirugía bariátrica. Usted solicita
            directamente con el prestamista y normalmente recibe respuesta en minutos.
          </li>
          <li>
            <strong>Fondos HSA y FSA.</strong> La cirugía bariátrica es generalmente un gasto
            médico elegible, así que puede usar dólares de salud antes de impuestos. Confirme
            los detalles con el administrador de su plan.
          </li>
          <li>
            <strong>Préstamo personal o línea de crédito.</strong> Algunos pacientes consiguen
            una mejor tasa en su propio banco o cooperativa de crédito. Vale la pena comparar.
          </li>
          <li>
            <strong>Beneficios del empleador.</strong> Algunos empleadores ofrecen beneficios de
            control de peso o de cirugía fuera del plan de salud regular. Pregunte a recursos
            humanos antes de asumir que no tiene cobertura.
          </li>
        </ul>
      </EsSection>

      <EsSection heading="Requisitos para el pago propio" id="requisitos">
        <p>
          El pago propio elimina el papeleo del seguro, pero no elimina los requisitos médicos
          que hacen segura la cirugía. Antes de su fecha de cirugía necesitará:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>Un IMC de 30 o más.</li>
          <li>Completar el programa de dieta de 3 días, incluido en su precio de pago propio.</li>
          <li>Una evaluación psicológica. Pídanos nuestra lista de referidos si la necesita.</li>
          <li>Autorización médica, análisis de laboratorio y un electrocardiograma dentro de los 30 días previos a la cirugía.</li>
          <li>Cualquier prueba adicional que el Dr. Fitzer indique según su historial médico. Él le dirá en su consulta exactamente qué aplica en su caso.</li>
        </ul>
        <p>
          Como los pacientes de pago propio no esperan la autorización del seguro, la mayoría
          pasa de la consulta a la cirugía mucho más rápido que por la vía del seguro. Si está
          comparando caminos, vea nuestra página de{" "}
          <Link to="/es/requisitos-para-cirugia" className="text-brand-mid font-medium hover:underline">requisitos para la cirugía</Link>{" "}
          y la de{" "}
          <Link to="/es/seguro-para-cirugia-bariatrica" className="text-brand-mid font-medium hover:underline">seguro para cirugía bariátrica</Link>.
        </p>
      </EsSection>

      <EsSection heading="Su camino hacia la cirugía" id="camino">
        <ol className="list-decimal space-y-2 pl-6">
          <li>
            Programe una consulta. Llame al{" "}
            <a href="tel:7037099771" className="text-brand-mid font-medium hover:underline">(703) 709-9771</a>{" "}
            o <Link to="/es/contacto" className="text-brand-mid font-medium hover:underline">pida una cita en línea</Link>.
          </li>
          <li>Asista a su consulta. El Dr. Fitzer revisará su historial, hablará con usted sobre qué procedimiento le conviene y le indicará las pruebas adicionales que necesite.</li>
          <li>Reciba una cotización por escrito. Nuestro consultorio confirma su total de pago propio y le explica el calendario de pagos y las opciones de financiamiento.</li>
          <li>Complete los requisitos: evaluación psicológica, autorización médica, laboratorios, electrocardiograma y el programa de dieta de 3 días.</li>
          <li>Programe la cirugía. El primer pago de $3,900 vence al programar.</li>
          <li>Última cita preoperatoria. Tres semanas antes de la cirugía confirmamos que todo esté listo y se cobra el segundo pago de $3,900.</li>
          <li>El día de la cirugía en INOVA Fair Oaks Hospital. ¡Anímese! Y planee una estancia de un día en el hospital.</li>
          <li>Seguimiento. Durante el primer año, sus visitas postoperatorias de rutina con el Dr. Fitzer están incluidas en su precio de pago propio.</li>
        </ol>
      </EsSection>

      <EsSection id="norte-de-virginia" heading="Cirugía bariátrica de pago propio en el norte de Virginia">
        <p>
          Todos los procedimientos de pago propio se realizan en INOVA Fair Oaks Hospital en Fairfax. Los procedimientos ambulatorios como la ESG, el TORe, el balón Orbera y la LAP-BAND suelen significar volver a casa el mismo día, mientras que la manga y el bypass incluyen una noche en el hospital. Los pacientes que vienen de fuera del área pueden hacer la primera consulta por telesalud y planear un solo viaje a Herndon para la cirugía.
        </p>
        <p>
          ¿Preguntas sobre el pago propio? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_selfpay_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection heading="Preguntas frecuentes" id="preguntas-frecuentes">
        <FaqList faqs={FAQS} />
      </EsSection>

      <EsSection heading="Pida una cotización de pago propio" id="cotizacion">
        <p className="text-center">
          Díganos cómo contactarle y qué procedimiento está considerando. Nuestro consultorio le
          contactará para responder sus preguntas, confirmar los precios vigentes y programar
          una consulta con el Dr. Fitzer.
        </p>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="mx-auto mt-6 max-w-2xl rounded-2xl border border-border bg-card p-4 text-left shadow-sm md:p-5"
          aria-label="Formulario de consulta sobre cirugía bariátrica de pago propio"
        >
          {sent ? (
            <div className="py-8 text-center">
              <h3 className="font-serif text-2xl text-brand-deep">Solicitud recibida</h3>
              <p className="mt-2 text-muted-foreground leading-[1.6]">
                Gracias. Nuestro consultorio recibió su consulta de pago propio y le
                contactará en breve.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label htmlFor="pago-propio-nombre" className="text-sm">
                  <span className="mb-1 block font-medium">Nombre<span aria-hidden="true" className="text-brand">*</span></span>
                  <input id="pago-propio-nombre" required type="text" name="name" autoComplete="name" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
                </label>
                <label htmlFor="pago-propio-telefono" className="text-sm">
                  <span className="mb-1 block font-medium">Teléfono</span>
                  <input id="pago-propio-telefono" type="tel" name="phone" autoComplete="tel" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
                </label>
              </div>
              <label htmlFor="pago-propio-email" className="block text-sm">
                <span className="mb-1 block font-medium">Correo electrónico<span aria-hidden="true" className="text-brand">*</span></span>
                <input id="pago-propio-email" required type="email" name="email" autoComplete="email" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
              </label>
              <label htmlFor="pago-propio-procedimiento" className="block text-sm">
                <span className="mb-1 block font-medium">Procedimiento de interés</span>
                <select id="pago-propio-procedimiento" name="procedure" className="w-full rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand">
                  <option value="">Aún no estoy seguro/a</option>
                  <option>Manga gástrica</option>
                  <option>Bypass gástrico</option>
                  <option>Lap-Band (banda gástrica)</option>
                  <option>Cirugía de revisión</option>
                  <option>Gastroplastia endoscópica en manga (ESG)</option>
                  <option>TORe</option>
                  <option>Balón gástrico Orbera</option>
                </select>
              </label>
              <label htmlFor="pago-propio-mensaje" className="block text-sm">
                <span className="mb-1 block font-medium">Preguntas o comentarios<span aria-hidden="true" className="text-brand">*</span></span>
                <textarea id="pago-propio-mensaje" required name="message" rows={4} maxLength={2000} className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 outline-none focus:border-brand" />
              </label>
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <div className="flex justify-center">
                <LazyTurnstile
                  siteKey={turnstileSiteKey}
                  triggerRef={formRef}
                  onSuccess={setTurnstileToken}
                  onError={() => setError("La verificación de seguridad falló. Inténtelo de nuevo.")}
                  onExpire={() => setTurnstileToken(null)}
                />
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <button type="submit" disabled={sending || !turnstileToken} className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60">
                  {sending ? "Enviando..." : "Pedir una Cotización de Pago Propio"}
                </button>
                <a href="tel:7037099771" className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90">
                  Llamar al (703) 709-9771
                </a>
              </div>
            </div>
          )}
        </form>
        <p className="mt-5 text-center text-sm text-muted-foreground leading-[1.6]">
          171 Elden Street, Suite 3B, Herndon, VA 20170 · Cirugía realizada en INOVA Fair Oaks Hospital, Fairfax, Virginia
        </p>
        <p className="mt-4 text-sm italic text-muted-foreground leading-[1.6]">
          Aviso médico: la información de esta página es de carácter general y no constituye
          consejo médico. Los precios mostrados son un estimado de los cargos de pago propio y
          pueden cambiar; su costo final depende del procedimiento realizado, la duración de su
          estancia y los servicios facturados por terceros, incluidos el hospital y el grupo de
          anestesiología. La candidatura para la cirugía bariátrica la determina el Dr. Fitzer
          de forma individual.
        </p>
      </EsSection>
    </EsPageShell>
  );
}
